import React, { useCallback, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../../pages/PageHeader";
import Loader from "../../common/Loader";
import { AdminHelpCenter } from "../../utils/APIClient";
import { triggerAlert } from "../../utils";
import { confirm } from "../../common/ConfirmDialog";
import { helpTableStyles } from "./tableStyles";

/**
 * Help Center → Categories.
 *
 * Categories group the video library and become the filter chips customers
 * see. A category in use cannot be deleted (the FK is PROTECT server-side);
 * deactivating hides it from customers without orphaning its videos.
 */
export default function HelpCategories() {
  document.title = "Digital Employees — Help Center Categories";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [editing, setEditing] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await AdminHelpCenter.listCategories();
      const list = r?.data?.results || [];
      list.forEach((v, i) => { v.Num = i + 1; });
      setRows(list);
    } catch (e) {
      setRows([]);
      triggerAlert("error", "Oops...", "Failed to load categories.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const resetForm = () => { setEditing(null); setName(""); setSortOrder(""); };

  const startEdit = (row) => {
    setEditing(row);
    setName(row.name);
    setSortOrder(String(row.sort_order ?? ""));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { triggerAlert("error", "Oops...", "Enter a category name."); return; }
    setSaving(true);
    try {
      const body = { name: trimmed, sort_order: Number(sortOrder) || 0 };
      if (editing) {
        await AdminHelpCenter.updateCategory(editing.id, body);
        triggerAlert("success", "Saved", "Category updated.");
      } else {
        await AdminHelpCenter.createCategory(body);
        triggerAlert("success", "Added", "Category created.");
      }
      resetForm();
      fetchRows();
    } catch (err) {
      const msg = err?.response?.data?.message || "Could not save the category.";
      triggerAlert("error", "Oops...", msg);
    } finally { setSaving(false); }
  };

  const toggleActive = async (row) => {
    try {
      await AdminHelpCenter.updateCategory(row.id, { is_active: !row.is_active });
      fetchRows();
    } catch (e) { triggerAlert("error", "Oops...", "Could not update."); }
  };

  const onDelete = async (row) => {
    const ok = await confirm({
      title: "Delete category?",
      message: `Delete "${row.name}"? This cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await AdminHelpCenter.deleteCategory(row.id);
      triggerAlert("success", "Deleted", "Category removed.");
      if (editing?.id === row.id) resetForm();
      fetchRows();
    } catch (err) {
      // 409 means videos still point at it — the message says how many.
      const msg = err?.response?.data?.message || "Delete failed.";
      triggerAlert("error", "Oops...", msg);
    }
  };

  const columns = [
    { name: "S.No", width: "70px", selector: (r) => r.Num, cell: (r) => <span>{r.Num}</span> },
    {
      name: "Name", sortable: true, selector: (r) => r.name,
      cell: (r) => <span style={{ fontWeight: 600 }}>{r.name}</span>,
    },
    {
      name: "Slug", sortable: true, selector: (r) => r.slug,
      cell: (r) => <span style={{ fontFamily: "monospace", fontSize: 12 }}>{r.slug}</span>,
    },
    { name: "Videos", width: "100px", sortable: true, selector: (r) => r.video_count },
    { name: "Order", width: "100px", sortable: true, selector: (r) => r.sort_order },
    {
      name: "Status", width: "110px", sortable: true, selector: (r) => r.is_active,
      cell: (r) => (r.is_active
        ? <span className="badge bg-success">Active</span>
        : <span className="badge bg-secondary">Hidden</span>),
    },
    {
      name: "Action",
      cell: (r) => (
        <div className="d-flex gap-2">
          <button className="btn btn-outline-primary btn-sm" onClick={() => startEdit(r)}>Edit</button>
          <button
            className={`btn btn-sm ${r.is_active ? "btn-outline-warning" : "btn-outline-success"}`}
            onClick={() => toggleActive(r)}
          >
            {r.is_active ? "Hide" : "Show"}
          </button>
          <button className="btn btn-outline-danger btn-sm" onClick={() => onDelete(r)}>Delete</button>
        </div>
      ),
    },
  ];

  return (
    <div id="layout-wrapper">
      <Header />
      <Sidebar />
      <div className="main-content">
        <div className="page-content" style={{ backgroundColor: "#ededed" }}>
          <div className="container-fluid">
            <PageHeader mainHeading="Help Center Categories" heading="Help Center / Categories" />
            <div className="row"><div className="col-12"><div className="card"><div className="card-body">
              <div className="alert alert-info py-2 mb-3">
                Categories group help videos and appear as filter chips in the customer
                portal. Hidden categories stay attached to their videos but disappear
                from the customer filters.
              </div>

              <form onSubmit={onSubmit} className="row align-items-end mb-3 g-2">
                <div className="col-md-6">
                  <label className="form-label">Category name</label>
                  <input
                    className="form-control"
                    placeholder="e.g. Billing"
                    maxLength={80}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Sort order</label>
                  <input
                    type="number" min="0" className="form-control" placeholder="0"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                  />
                </div>
                <div className="col-md-3 text-end d-flex gap-2">
                  <button type="submit" className="btn btn-primary flex-grow-1" disabled={saving}>
                    {saving ? "Saving..." : editing ? "Update" : "Add category"}
                  </button>
                  {editing && (
                    <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                      Cancel
                    </button>
                  )}
                </div>
              </form>

              {!loading ? (
                <DataTable
                  columns={columns} data={rows} pagination fixedHeader
                  fixedHeaderScrollHeight="640px" persistTableHead
                  customStyles={helpTableStyles}
                  noDataComponent={<div style={{ padding: 24 }}>No categories yet. Add one above.</div>}
                />
              ) : <div className="loader-overlay text-white"><Loader /></div>}
            </div></div></div></div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}
